import { FormattedMessage } from 'react-intl'
import { Link, useLocation, useMatches } from '@tanstack/react-router'

import { Button } from '@gto/ui/button'
import { cn } from '@gto/ui/cn'
import { useMediaQuery } from '@gto/ui/hooks/use-media-query'
import { Paper } from '@gto/ui/paper'
import { Typography } from '@gto/ui/typography'

import { EdgeBalance } from '@/components/edge-balance'
import { useAuth } from '@/hooks/use-auth'
import { useEdgeBalance } from '@/hooks/use-edge-balance'

export const RewardsPageHeader = () => {
  const isSmAndLower = useMediaQuery('down', 'sm')
  const matches = useMatches()
  const { pathname } = useLocation()
  const { emailValidated } = useAuth()
  const isRewards = matches.some((route) => route.id === '/_layout/_private/app/rewards/')
  const isEdgeRoute = pathname.startsWith('/app/rewards/edge')
  const { isRedemptionUnlocked, edgeEarned, edgeToUnlock } = useEdgeBalance()
  const isUnlockProgressIncomplete =
    edgeToUnlock > 0 && edgeEarned < edgeToUnlock && !isRedemptionUnlocked

  const titleId = isEdgeRoute
    ? 'common.labels.redeemEdge'
    : isRewards
      ? 'common.labels.rewardsSummary'
      : 'common.labels.redeemEdge'

  const redeemEdgeTitleBase =
    'font-heading text-[42px] font-medium leading-[50px] tracking-[0.65px] align-middle text-text-primary'
  const redeemEdgeHighlightBase =
    'font-heading text-[42px] font-medium leading-[50px] tracking-[0.65px] align-middle'
  const redeemEdgePageHighlightTypography = cn(redeemEdgeHighlightBase, 'text-primary-main')
  const redeemEdgePageHighlightTypographyUnlock = cn(redeemEdgeHighlightBase, 'text-cyan-300')

  const renderRedeemEdgeHeading = (
    align: 'left' | 'center',
    options: { gutterBottom: boolean; flex1?: boolean; highlightClassName?: string },
  ) => (
    <Typography
      component="h1"
      gutterBottom={options.gutterBottom}
      variant="h4"
      className={cn(
        redeemEdgeTitleBase,
        align === 'left' ? 'text-left' : 'text-center',
        align === 'center' && 'w-full',
        options.flex1 === true && 'min-w-0 flex-1',
      )}
    >
      <FormattedMessage
        id="common.labels.redeemEdge"
        values={{
          highlight: (chunks) => (
            <span className={options.highlightClassName ?? redeemEdgePageHighlightTypography}>
              {chunks}
            </span>
          ),
        }}
      />
    </Typography>
  )

  if (isEdgeRoute) {
    return (
      <div className="flex w-full flex-row items-center justify-between gap-4 sm:flex-col sm:items-start">
        {renderRedeemEdgeHeading('left', {
          gutterBottom: false,
          flex1: emailValidated,
          highlightClassName: isUnlockProgressIncomplete
            ? redeemEdgePageHighlightTypographyUnlock
            : undefined,
        })}
        {emailValidated && (
          <div className="shrink-0 self-auto sm:self-end">
            <BalanceCard
              compact
              coinType={isUnlockProgressIncomplete ? 'excellence' : 'edge'}
              showRedeemButton={false}
            />
          </div>
        )}
      </div>
    )
  }

  const defaultTitle =
    titleId === 'common.labels.redeemEdge' ? (
      renderRedeemEdgeHeading('left', { gutterBottom: false, flex1: emailValidated })
    ) : (
      <Typography
        className="min-w-0 flex-1"
        component="h1"
        gutterBottom={false}
        variant={isSmAndLower ? 'h5' : 'h4'}
      >
        <FormattedMessage
          id={titleId}
          values={{
            highlight: (chunks) => <span>{chunks}</span>,
          }}
        />
      </Typography>
    )

  return (
    <div className="flex w-full flex-row items-center justify-between gap-4 sm:flex-col sm:items-start">
      {defaultTitle}
      {emailValidated && (
        <div className="w-auto shrink-0 self-auto sm:self-end">
          <BalanceCard
            compact
            showRedeemButton
          />
        </div>
      )}
    </div>
  )
}

type BalanceCardProps = {
  compact?: boolean
  showRedeemButton?: boolean
  coinType?: 'edge' | 'excellence'
}

const BalanceCard = ({
  compact = false,
  showRedeemButton = true,
  coinType = 'edge',
}: BalanceCardProps) => {
  const isSmAndLower = useMediaQuery('down', 'sm')
  const { balance } = useEdgeBalance()

  return (
    <Paper
      className={cn(
        'flex items-center gap-6 rounded-md border border-divider p-4',
        compact ? 'pr-8' : 'pr-4 md:w-full md:flex-[1_0_auto] md:justify-between',
      )}
    >
      <EdgeBalance
        withSubtitle
        balance={balance}
        coinType={coinType}
        iconSize={40}
        typoProps={{
          variant: 'h4',
        }}
      />
      {showRedeemButton && (
        <Button
          viewTransition
          color="primary"
          component={Link}
          size={isSmAndLower ? 'small' : 'large'}
          to="/app/rewards/edge"
          variant="contained"
        >
          <FormattedMessage id="common.controls.redeemEdgeNow" />
        </Button>
      )}
    </Paper>
  )
}
